import { useTranslation } from 'react-i18next'

import type { FormPage, FormQuestion } from '@percent/admin-dashboard/api/actions/grantProgram/grantProgram.types'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { Flex, Loader, MarkdownPreview, Typography } from '@percent/lemonade'
import { useQuery } from '@percent/utility'
import { PreviewQuestion } from './previewQuestion/PreviewQuestion'

type PreviewFormProps = {
  formId: string
}

export function PreviewForm({ formId }: PreviewFormProps) {
  const { grantProgramService } = useServices()
  const { t } = useTranslation()

  const {
    data: formData,
    isLoading,
    error
  } = useQuery(
    ['form-preview', formId],
    grantProgramService.getFormById,
    {
      formId
    },
    {
      enabled: !!formId
    }
  )

  if (isLoading) return <Loader />

  if (!formId || !formData || error) {
    return (
      <Flex direction="column" gap={16}>
        <Typography variant="h4">{t('grants.programDetails.taskTemplates.formNotFound')}</Typography>
      </Flex>
    )
  }

  const sortedPages = formData.pages?.sort((a: FormPage, b: FormPage) => a.order - b.order) ?? []

  return (
    <Flex direction="column" gap={24}>
      <Typography variant="h2">{formData.title}</Typography>
      <Flex direction="column" gap={24}>
        {sortedPages.map((page: FormPage) => (
          <Flex key={page.id} direction="column" gap={16}>
            {page.title !== formData.title && <MarkdownPreview content={page.title} fontSize="bodyLarge" />}
            {page.description && <MarkdownPreview content={page.description} />}
            {page.questions
              ?.sort((a: FormQuestion, b: FormQuestion) => a.order - b.order)
              .map((question: FormQuestion) => (
                <PreviewQuestion key={question.id} question={question} />
              ))}
          </Flex>
        ))}
      </Flex>
    </Flex>
  )
}
